import { createContext, useContext, useEffect, useState } from 'react'

export interface AuthUser {
  email?: string
  name?: string
  preferred_username?: string
  sub?: string
  [k: string]: unknown
}

export interface AuthState {
  user: AuthUser | null
  role: string | null
  /** Whether role checks are actually denying anything server-side. */
  enforcementActive: boolean
  /**
   * Whether to show admin-only controls.
   *
   * TRUE when enforcement is off — every user genuinely has full access then, so
   * hiding controls would be a lie in the other direction. Only once enforcement
   * is live does a non-admin get a reduced UI. This mirrors the server: the
   * middleware also permits everything while enforcement is inactive.
   */
  isAdmin: boolean
  loading: boolean
}

const DEFAULT: AuthState = {
  user: null,
  role: null,
  enforcementActive: false,
  // Default to permissive while loading so controls do not flicker
  // visible->hidden->visible on every page load.
  isAdmin: true,
  loading: true,
}

const AuthContext = createContext<AuthState>(DEFAULT)

/** Read the current user and role. Safe to call from any page. */
export function useAuth(): AuthState {
  return useContext(AuthContext)
}

/** Convenience: true when admin-only controls should be rendered. */
export function useIsAdmin(): boolean {
  return useContext(AuthContext).isAdmin
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(DEFAULT)

  useEffect(() => {
    let cancelled = false
    // /auth/me is NOT under /api and returns 200 {authenticated:false} rather
    // than 401 when logged out, so branch on the body flag.
    fetch('/auth/me', { credentials: 'same-origin' })
      .then(r => (r.status === 200 ? r.json() : null))
      .then(body => {
        if (cancelled) return
        if (body?.authenticated === true) {
          const role = body.role || null
          const enforcementActive = body.enforcement_active === true
          setState({
            user: body.user || null,
            role,
            enforcementActive,
            isAdmin: !enforcementActive || role === 'admin',
            loading: false,
          })
        } else {
          setState({ ...DEFAULT, loading: false })
        }
      })
      .catch(() => {
        // Fail permissive and let the server be the authority: a failed role
        // lookup must not present a read-only UI to a genuine admin. Any action
        // they attempt is still checked server-side.
        if (!cancelled) setState({ ...DEFAULT, loading: false })
      })
    return () => { cancelled = true }
  }, [])

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>
}

export default AuthProvider
